import type { ReactNode } from "react";
import { cx } from "@/shared/lib/cx";
import { Icon } from "../Icon";

export function Field({ label, error, hint, full, required, children, className }: {
  label: ReactNode; error?: string; hint?: ReactNode;
  /** Span the whole row inside a <FormGrid>. */
  full?: boolean;
  /** Marks the label with an asterisk (validation stays with the caller). */
  required?: boolean;
  children: ReactNode; className?: string;
}) {
  return (
    <label className={cx("field", full && "full", className)}>
      <span className="field-label">{label}{required && <span className="field-required" aria-hidden>*</span>}</span>
      {children}
      <FieldMessage error={error} hint={hint} />
    </label>
  );
}

/** Like <Field>, but a <div role="group"> so it can hold several controls. */
export function FieldGroup({ label, hint, error, required, children, actions }: {
  label: ReactNode; hint?: ReactNode; error?: string; required?: boolean; children: ReactNode;
  /** Controls aligned with the label (e.g. a "Xáo trộn" button). */
  actions?: ReactNode;
}) {
  return (
    <div className="field" role="group" aria-label={typeof label === "string" ? label : undefined}>
      <div className="row gap-sm">
        <span className="field-label">{label}{required && <span className="field-required" aria-hidden>*</span>}</span>
        {actions && <><span className="spacer" />{actions}</>}
      </div>
      <div className="stack gap-xs">{children}</div>
      <FieldMessage error={error} hint={hint} />
    </div>
  );
}

export function FieldMessage({ error, hint }: { error?: string; hint?: ReactNode }) {
  if (error) return <span className="field-error" role="alert"><Icon name="alert" size={14} />{error}</span>;
  if (hint) return <span className="field-hint">{hint}</span>;
  return null;
}

/** Auto-fitting columns of fields. */
export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("form-grid", className)}>{children}</div>;
}
